import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import type { BinColor, SoppelLevel } from '../../data/schemas';
import { resolveSoppelEndpoints } from '../../data/loadData';
import { useSplineDrive } from '../core/splineDrive/useSplineDrive';
import type { LevelRunnerApi } from '../core/levelRunner/useLevelRunner';
import { playVoice, speak } from '../core/voice';
import {
  SoppelScene,
  buildInitialBins,
  buildInitialLitter,
  finishLandmarkWorld,
  type BinRuntime,
  type LitterRuntime,
} from './SoppelScene';
import type { SplinePose } from '../core/splineDrive/useSplineDrive';
import { finishApproachPose } from './finishManeuver';
import { RecyclePlay } from './RecyclePlay';
import { GOOD_JOB_TEXT, binPraise } from './copy';
import { DUMP_DURATION_MS, DUMP_FALL_START, DUMP_LAND_AT } from './dumpAnimation';
import {
  playCelebrateSound,
  playDumpSound,
  playHornSound,
  playPickupSound,
  playRumbleSound,
  playSurpriseSound,
  playWobbleSound,
} from './sounds';

export interface SoppelbilenGameProps {
  level: SoppelLevel;
  api: LevelRunnerApi;
}

type StopPhase = 'litter' | 'bins' | 'done';

function useDriveLoop(active: boolean, advance: (dt: number) => void) {
  useEffect(() => {
    if (!active) return undefined;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      advance(dt);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, advance]);
}

/** "Good job!" in Norwegian, shown right after the load is dumped (celebration phase). */
const GoodJobBanner: React.FC = () => (
  <div
    className="pointer-events-none absolute inset-x-0 top-[18%] z-30 flex justify-center px-4"
    data-name="good-job-banner"
    role="status"
  >
    <motion.div
      initial={{ scale: 0.2, opacity: 0, rotate: -6 }}
      animate={{ scale: 1, opacity: 1, rotate: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 14 }}
      className="rounded-3xl bg-white/95 px-10 py-5 text-center font-display text-5xl font-bold text-[#6441A5] shadow-2xl sm:text-6xl"
    >
      {GOOD_JOB_TEXT} <span aria-hidden>🎉</span>
    </motion.div>
  </div>
);

/** Top-centre progress: one bin icon per stop, grey → filled bounce. */
const ProgressStrip: React.FC<{
  stops: SoppelLevel['stops'];
  doneStops: Set<number>;
}> = ({ stops, doneStops }) => {
  if (stops.length === 0) return null;
  return (
    <div
      className="pointer-events-none absolute top-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/80 px-3 py-2 shadow"
      data-name="progress-strip"
      aria-hidden
    >
      {stops.map((stop, i) => {
        const done = doneStops.has(i);
        const color = stop.bins.find((b) => b.target)?.color ?? stop.bins[0]?.color ?? 'green';
        const hex = color === 'green' ? '#3FB950' : color === 'blue' ? '#2F81F7' : '#F85149';
        return (
          <span
            key={i}
            className={done ? 'inline-block animate-bounce' : 'inline-block'}
            style={{
              width: 28,
              height: 34,
              borderRadius: 6,
              background: done ? hex : '#D1D5DB',
              border: '2px solid #374151',
              transform: done ? 'scale(1.08)' : 'scale(1)',
              transition: 'background 0.25s, transform 0.25s',
            }}
          />
        );
      })}
    </div>
  );
};

export const SoppelbilenGame: React.FC<SoppelbilenGameProps> = ({ level, api }) => {
  const endpoints = useMemo(() => resolveSoppelEndpoints(level), [level]);
  const stopTs = useMemo(() => level.stops.map((s) => s.t), [level.stops]);
  const drive = useSplineDrive({
    preset: level.route.preset,
    speed: level.route.speed,
    stopTs,
  });

  const [bins, setBins] = useState<BinRuntime[]>(() => buildInitialBins(level));
  const [litter, setLitter] = useState<LitterRuntime[]>(() =>
    buildInitialLitter(level, drive.route.points)
  );
  const [stopPhase, setStopPhase] = useState<StopPhase>('bins');
  const [armLift, setArmLift] = useState(0);
  const [dumping, setDumping] = useState(false);
  const [dumpingBin, setDumpingBin] = useState<{ stopIndex: number; binIndex: number } | null>(null);
  /** Dumps whose rubbish has landed in the hopper — drives the truck's growing pile. */
  const [deposited, setDeposited] = useState(0);
  const dumpingRef = useRef<{ stopIndex: number; binIndex: number } | null>(null);
  const [celebrating, setCelebrating] = useState(false);
  const [activeStop, setActiveStop] = useState<number | null>(null);
  const [garageDoorOpen, setGarageDoorOpen] = useState(0);
  const [headlightsBlink, setHeadlightsBlink] = useState(false);
  const [tipDump, setTipDump] = useState(false);
  const [finishManeuvering, setFinishManeuvering] = useState(false);
  const [finishReady, setFinishReady] = useState(false);
  const [finishPose, setFinishPose] = useState<SplinePose | null>(null);
  const [doneStops, setDoneStops] = useState<Set<number>>(() => new Set());
  const completingRef = useRef(false);
  const stopsDoneRef = useRef<Set<number>>(new Set());
  const handledArrivalRef = useRef<number | null>(null);
  const finishStartedRef = useRef(false);
  const armRafRef = useRef<number | null>(null);
  const finishRafRef = useRef<number | null>(null);
  const beginFinishRef = useRef(api.beginFinish);
  beginFinishRef.current = api.beginFinish;

  const needsLitterFirst = level.mode === 'pickLitter' || level.mode === 'mixed';

  const binsRef = useRef(bins);
  binsRef.current = bins;
  const litterRef = useRef(litter);
  litterRef.current = litter;

  const targetBinCount = Math.max(1, bins.filter((b) => b.target).length);
  const loadAmount = Math.min(1, deposited / targetBinCount);

  useEffect(() => {
    drive.reset();
    setBins(buildInitialBins(level));
    setLitter(buildInitialLitter(level, drive.route.points));
    setStopPhase(needsLitterFirst ? 'litter' : 'bins');
    if (armRafRef.current != null) cancelAnimationFrame(armRafRef.current);
    armRafRef.current = null;
    dumpingRef.current = null;
    setDumpingBin(null);
    setDeposited(0);
    setArmLift(0);
    setDumping(false);
    setCelebrating(false);
    setActiveStop(null);
    setGarageDoorOpen(0);
    setHeadlightsBlink(false);
    setTipDump(false);
    setFinishManeuvering(false);
    setFinishReady(false);
    setFinishPose(null);
    setDoneStops(new Set());
    completingRef.current = false;
    stopsDoneRef.current = new Set();
    handledArrivalRef.current = null;
    finishStartedRef.current = false;
    if (finishRafRef.current != null) cancelAnimationFrame(finishRafRef.current);
    finishRafRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level.id]);

  // Auto-start after voice if action is auto
  useEffect(() => {
    if (api.phase !== 'start') return;
    if (endpoints.start.action !== 'auto') return;
    const t = window.setTimeout(() => {
      setGarageDoorOpen(1);
      api.startPlay();
    }, 2200);
    return () => window.clearTimeout(t);
  }, [api, endpoints.start.action]);

  const currentSign: BinColor | null = useMemo(() => {
    if (activeStop == null) return null;
    const showSign = level.mode === 'sortColor' || (level.mode === 'mixed' && stopPhase === 'bins');
    if (!showSign) return null;
    const target = bins.find((b) => b.stopIndex === activeStop && b.target && b.visual !== 'emptied');
    return target?.color ?? null;
  }, [activeStop, bins, level.mode, stopPhase]);

  useEffect(
    () => () => {
      if (armRafRef.current != null) cancelAnimationFrame(armRafRef.current);
    },
    []
  );

  /**
   * One bin → hopper dump (see dumpAnimation.ts for the timeline): the bin lifts to the
   * truck, the lid opens, the rubbish falls in (pile grows), the bin returns.
   */
  const praiseCountRef = useRef(0);
  const runDumpAnim = useCallback(
    (stopIndex: number, binIndex: number, onDone: () => void) => {
      if (armRafRef.current != null) cancelAnimationFrame(armRafRef.current);
      dumpingRef.current = { stopIndex, binIndex };
      setDumpingBin({ stopIndex, binIndex });
      setDumping(true);
      const start = performance.now();
      let fell = false;
      let landed = false;
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / DUMP_DURATION_MS);
        setArmLift(t);
        if (!fell && t >= DUMP_FALL_START) {
          fell = true;
          playDumpSound();
        }
        if (!landed && t >= DUMP_LAND_AT) {
          landed = true;
          setDeposited((n) => n + 1);
          void speak(binPraise(praiseCountRef.current++));
        }
        if (t < 1) {
          armRafRef.current = requestAnimationFrame(step);
        } else {
          setArmLift(0);
          setDumping(false);
          setDumpingBin(null);
          dumpingRef.current = null;
          armRafRef.current = null;
          onDone();
        }
      };
      armRafRef.current = requestAnimationFrame(step);
    },
    []
  );

  const goCelebrate = useCallback(() => {
    if (completingRef.current) return;
    completingRef.current = true;
    setCelebrating(true);
    playCelebrateSound();
    api.beginCelebration();
  }, [api]);
  const goCelebrateRef = useRef(goCelebrate);
  goCelebrateRef.current = goCelebrate;

  const markStopComplete = useCallback(
    (stopIndex: number) => {
      stopsDoneRef.current.add(stopIndex);
      setDoneStops(new Set(stopsDoneRef.current));
      setActiveStop(null);
      setStopPhase('done');
      window.setTimeout(() => {
        drive.resume();
      }, 400);
    },
    [drive]
  );

  useEffect(() => {
    if (drive.arrivedStopIndex == null) return;
    if (handledArrivalRef.current === drive.arrivedStopIndex) return;
    handledArrivalRef.current = drive.arrivedStopIndex;

    const idx = drive.arrivedStopIndex;
    setActiveStop(idx);
    api.pokeActivity();
    // Speak the tap cue immediately (not only as on-screen text)
    void playVoice(level.id, 'hint', level.voice.hint);
    const stop = level.stops[idx];
    const litterHere = (stop.litter ?? 0) > 0;
    if (needsLitterFirst && litterHere) {
      setStopPhase('litter');
    } else if (stop.bins.some((b) => b.target || level.mode === 'tap')) {
      setStopPhase('bins');
    } else if (stop.bins.length > 0) {
      setStopPhase('bins');
    } else {
      markStopComplete(idx);
    }
  }, [drive.arrivedStopIndex, api, level.stops, level.mode, level.id, level.voice.hint, needsLitterFirst, markStopComplete]);

  // After last stop: turn 180° (rear toward plant) then reverse into the hall for dump.
  // Do NOT depend on `api` / `goCelebrate` — beginFinish() changes phase → new api
  // identity → effect cleanup was cancelling the RAF while finishStartedRef stayed true,
  // so finishReady never flipped and the dump tap stayed dead.
  useEffect(() => {
    if (!drive.finished) return;
    if (stopsDoneRef.current.size < level.stops.length) return;
    if (finishStartedRef.current) return;
    finishStartedRef.current = true;

    if (endpoints.finish.action === 'none') {
      goCelebrateRef.current();
      return undefined;
    }

    beginFinishRef.current();
    setFinishManeuvering(true);
    setFinishReady(false);
    setHeadlightsBlink(true);

    const from = { x: drive.pose.x, z: drive.pose.z, yaw: drive.pose.yaw };
    const landmark = finishLandmarkWorld(drive.route.points, endpoints.finish.preset);
    const turnedYaw = landmark.yaw + Math.PI;
    const durationMs = 2200;
    const start = performance.now();

    const step = (now: number) => {
      const u = Math.min(1, (now - start) / durationMs);
      setFinishPose(finishApproachPose(from, landmark, u));
      if (u < 1) {
        finishRafRef.current = requestAnimationFrame(step);
      } else {
        finishRafRef.current = null;
        setFinishManeuvering(false);
        setFinishReady(true);
        setHeadlightsBlink(false);
        setFinishPose({ x: landmark.x, z: landmark.z, yaw: turnedYaw, t: 1 });
      }
    };
    finishRafRef.current = requestAnimationFrame(step);

    return () => {
      if (finishRafRef.current == null) return;
      // Aborted mid-maneuver (e.g. level change) — clear guard so a retry can start.
      cancelAnimationFrame(finishRafRef.current);
      finishRafRef.current = null;
      finishStartedRef.current = false;
      setFinishManeuvering(false);
      setFinishReady(false);
    };
    // Intentionally omit drive.pose / api / goCelebrate — capture once when route ends.
    // Depending on `api` re-ran this effect when beginFinish() flipped phase and killed the RAF.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drive.finished, doneStops.size, endpoints.finish.action, endpoints.finish.preset, level.stops.length]);

  const stopFullyCleared = useCallback(
    (stopIndex: number, nextBins: BinRuntime[], nextLitter: LitterRuntime[]) => {
      const litterLeft = nextLitter.filter((l) => l.stopIndex === stopIndex && !l.collected).length;
      const targetsLeft = nextBins.filter(
        (b) => b.stopIndex === stopIndex && b.target && b.visual !== 'emptied'
      ).length;
      if (level.mode === 'pickLitter') return litterLeft === 0;
      if (level.mode === 'mixed') return litterLeft === 0 && targetsLeft === 0;
      return targetsLeft === 0;
    },
    [level.mode]
  );

  const onLitterTap = useCallback(
    (id: string) => {
      if (activeStop == null || stopPhase !== 'litter') return;
      api.pokeActivity();
      playPickupSound();
      setLitter((prev) => {
        const next = prev.map((l) => (l.id === id ? { ...l, collected: true } : l));
        const left = next.filter((l) => l.stopIndex === activeStop && !l.collected).length;
        if (left === 0) {
          if (level.mode === 'pickLitter') {
            window.setTimeout(() => markStopComplete(activeStop), 200);
          } else {
            setStopPhase('bins');
            void playVoice(level.id, 'hint', level.voice.hint);
          }
        }
        return next;
      });
    },
    [activeStop, api, level.mode, level.id, level.voice.hint, markStopComplete, stopPhase]
  );

  const onBinTap = useCallback(
    (stopIndex: number, binIndex: number): 'ok' | 'wrong' | 'progress' => {
      if (activeStop !== stopIndex || stopPhase !== 'bins') return 'wrong';
      api.pokeActivity();
      // One dump at a time: extra taps while a bin is in the air are simply ignored.
      if (dumpingRef.current) return 'progress';

      const bin = bins.find((b) => b.stopIndex === stopIndex && b.binIndex === binIndex);
      if (!bin || bin.visual === 'emptied' || bin.visual === 'raising') return 'wrong';

      if (!bin.target) {
        playWobbleSound();
        return 'wrong';
      }

      if (bin.visual === 'tipped' || bin.visual === 'snowy') {
        setBins((prev) =>
          prev.map((b) =>
            b.stopIndex === stopIndex && b.binIndex === binIndex ? { ...b, visual: 'ready' } : b
          )
        );
        playPickupSound();
        return 'progress';
      }

      playPickupSound();
      setBins((prev) =>
        prev.map((b) =>
          b.stopIndex === stopIndex && b.binIndex === binIndex ? { ...b, visual: 'raising' as const } : b
        )
      );
      runDumpAnim(stopIndex, binIndex, () => {
        if (bin.surprise) playSurpriseSound();
        const markEmptied = (list: BinRuntime[]) =>
          list.map((b) =>
            b.stopIndex === stopIndex && b.binIndex === binIndex
              ? { ...b, visual: 'emptied' as const, shownSurprise: Boolean(b.surprise) }
              : b
          );
        setBins(markEmptied);
        if (stopFullyCleared(stopIndex, markEmptied(binsRef.current), litterRef.current)) {
          window.setTimeout(() => markStopComplete(stopIndex), 350);
        }
      });
      return 'ok';
    },
    [activeStop, api, bins, markStopComplete, runDumpAnim, stopFullyCleared, stopPhase]
  );

  const onTruckTap = useCallback(() => {
    if (api.phase === 'start' && endpoints.start.action === 'tapVehicle') {
      api.pokeActivity();
      playHornSound();
      setHeadlightsBlink(true);
      // Door rolls up, then drive out
      const start = performance.now();
      const openDoor = (now: number) => {
        const t = Math.min(1, (now - start) / 700);
        setGarageDoorOpen(t);
        if (t < 1) requestAnimationFrame(openDoor);
        else {
          setHeadlightsBlink(false);
          api.startPlay();
        }
      };
      requestAnimationFrame(openDoor);
      return;
    }

    if (
      api.phase === 'finish' &&
      endpoints.finish.action === 'tapToDump' &&
      finishReady &&
      !tipDump
    ) {
      api.pokeActivity();
      setTipDump(true);
      playRumbleSound();
      // The truck stays put at the ring while the hopper tips.
      // The hopper tips (~0.6 s), then the pile slides out into the pit (~0.9 s)
      window.setTimeout(() => setDeposited(0), 1750);
      window.setTimeout(() => {
        setTipDump(false);
        goCelebrate();
      }, 2200);
    }
  }, [
    api,
    endpoints.finish.action,
    endpoints.start.action,
    finishReady,
    goCelebrate,
    tipDump,
  ]);

  const driving =
    api.phase === 'play' &&
    !celebrating &&
    activeStop == null &&
    !drive.paused &&
    !drive.finished &&
    !finishManeuvering;

  useDriveLoop(driving, drive.advance);

  const [arrivalCue, setArrivalCue] = useState(false);
  useEffect(() => {
    if (activeStop == null) {
      setArrivalCue(false);
      return undefined;
    }
    setArrivalCue(true);
    const t = window.setTimeout(() => setArrivalCue(false), 3500);
    return () => window.clearTimeout(t);
  }, [activeStop, stopPhase]);

  const hintLabel = useMemo(() => {
    if (api.phase === 'start') {
      return api.idleHintVisible ? endpoints.start.voice : 'Trykk på bilen!';
    }
    if (api.phase === 'finish') {
      if (finishManeuvering) return 'Kjører inn…';
      if (!finishReady || tipDump) return null;
      return endpoints.finish.voice;
    }
    if (activeStop == null) return null;
    if (!(api.idleHintVisible || arrivalCue)) return null;
    if (stopPhase === 'litter') return 'Trykk på søppelet!';
    if (level.mode === 'sortColor') return 'Trykk riktig farge!';
    return 'Trykk på dunken!';
  }, [
    api.phase,
    api.idleHintVisible,
    arrivalCue,
    activeStop,
    stopPhase,
    level.mode,
    endpoints.start.voice,
    endpoints.finish.voice,
    finishManeuvering,
    finishReady,
    tipDump,
  ]);

  const showHand =
    (api.phase === 'start' && endpoints.start.action === 'tapVehicle') ||
    (api.phase === 'finish' &&
      endpoints.finish.action === 'tapToDump' &&
      finishReady &&
      !tipDump) ||
    (activeStop != null && (api.idleHintVisible || arrivalCue));

  const sceneStopPhase =
    activeStop == null ? 'driving' : stopPhase === 'done' ? 'done' : stopPhase;

  const worldPhase: 'start' | 'play' | 'finish' =
    api.phase === 'start' ? 'start' : api.phase === 'finish' ? 'finish' : 'play';

  const displayPose = finishPose ?? drive.pose;

  // Recycle has no drive: skip garage start and go straight into sorting
  useEffect(() => {
    if (level.mode !== 'recycle') return;
    if (api.phase !== 'start') return;
    const t = window.setTimeout(() => api.startPlay(), 600);
    return () => window.clearTimeout(t);
  }, [level.mode, api]);

  if (level.mode === 'recycle') {
    if (api.phase === 'start') {
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="font-display text-2xl text-white drop-shadow">{endpoints.start.voice}</p>
        </div>
      );
    }
    return (
      <div className="absolute inset-0 h-full w-full">
        <RecyclePlay
          level={level}
          onActivity={api.pokeActivity}
          onComplete={() => {
            if (completingRef.current) return;
            // soppel-10: finish.action none → straight to celebration
            goCelebrate();
          }}
        />
        {celebrating || api.phase === 'celebration' ? <GoodJobBanner /> : null}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 h-full w-full" data-name={`soppelbilen-${level.id}`}>
      <SoppelScene
        level={level}
        pose={displayPose}
        routePoints={drive.route.points}
        signColor={currentSign}
        armLift={armLift}
        dumpingBin={dumpingBin}
        dumping={dumping}
        bins={bins}
        litter={litter}
        celebrating={celebrating || api.phase === 'celebration'}
        activeStopIndex={activeStop}
        stopPhase={sceneStopPhase}
        loadAmount={loadAmount}
        worldPhase={worldPhase}
        canvasActive={api.phase !== 'reward'}
        garageDoorOpen={garageDoorOpen}
        finishPreset={endpoints.finish.preset}
        tipDump={tipDump}
        headlightsBlink={headlightsBlink}
        truckPulse={
          (api.phase === 'start' && !finishManeuvering) ||
          (api.phase === 'finish' && finishReady && !tipDump)
        }
        truckTapEnabled={
          (api.phase === 'start' && endpoints.start.action === 'tapVehicle') ||
          (api.phase === 'finish' &&
            endpoints.finish.action === 'tapToDump' &&
            finishReady &&
            !tipDump)
        }
        onTruckTap={onTruckTap}
        onBinTap={onBinTap}
        onLitterTap={onLitterTap}
      />

      <ProgressStrip stops={level.stops} doneStops={doneStops} />

      {celebrating || api.phase === 'celebration' ? <GoodJobBanner /> : null}

      {driving ? (
        <div
          className="pointer-events-none absolute top-24 left-1/2 z-20 -translate-x-1/2 rounded-full bg-white/85 px-5 py-2 font-display text-lg font-bold text-[#27A9E1] shadow"
          data-name="driving-cue"
        >
          Bilen kjører…
        </div>
      ) : null}

      {showHand && hintLabel ? (
        <div
          className="pointer-events-none absolute bottom-8 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-2"
          data-name="idle-hint-label"
        >
          <div className="animate-bounce text-6xl" aria-hidden>
            👆
          </div>
          <div className="rounded-2xl bg-white/95 px-6 py-3 font-display text-xl font-bold text-[#6441A5] shadow-lg">
            {hintLabel}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SoppelbilenGame;
